import API_URLS from '../../../config/apiConfig';
import api, { apiGet, apiPost } from '../../../utils/apiUtils';
import { ErrorHandler } from '../../../utils/errorHandler';
import type {
  EmailPipelineProcessResponse,
  EmailPipelineRecordDetailsResponse,
  EmailPipelineRecordsQuery,
  EmailPipelineRecordsResponse,
  EmailPipelineStatusResponse,
} from '../../../types/features/emailPipeline';
import { buildQueryString } from '../../../utils/buildQueryString';

const FAILURE_STATUSES = new Set(['error', 'failed', 'failure']);
const DEFAULT_PROCESS_FAILURE_MESSAGE = 'Failed to process email pipeline message';

const isFailureStatus = (value: unknown): boolean =>
  typeof value === 'string' && FAILURE_STATUSES.has(value.trim().toLowerCase());

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getFailureMessage = (response: EmailPipelineProcessResponse): string => {
  if (typeof response.message === 'string' && response.message.trim()) {
    return response.message;
  }

  if (typeof response.detail === 'string' && response.detail.trim()) {
    return response.detail;
  }

  if (typeof response.error === 'string' && response.error.trim()) {
    return response.error;
  }

  if (isRecord(response.result)) {
    const resultMessage = response.result.message;
    if (typeof resultMessage === 'string' && resultMessage.trim()) {
      return resultMessage;
    }

    const resultDetail = response.result.detail;
    if (typeof resultDetail === 'string' && resultDetail.trim()) {
      return resultDetail;
    }

    const resultError = response.result.error;
    if (typeof resultError === 'string' && resultError.trim()) {
      return resultError;
    }
  }

  return DEFAULT_PROCESS_FAILURE_MESSAGE;
};

const ensureProcessMessageSucceeded = (response: EmailPipelineProcessResponse): void => {
  if (response.success === false || isFailureStatus(response.status)) {
    throw new Error(getFailureMessage(response));
  }

  if (!isRecord(response.result)) {
    return;
  }

  if (response.result.success === false || isFailureStatus(response.result.status)) {
    throw new Error(getFailureMessage(response));
  }
};

class EmailPipelineService {
  async getStatus(): Promise<EmailPipelineStatusResponse> {
    try {
      const response = await apiGet(API_URLS.EMAIL_PIPELINE_STATUS);
      return response as EmailPipelineStatusResponse;
    } catch (error) {
      console.error('Failed to fetch email pipeline status:', error);
      ErrorHandler.handleApiError(error, 'Failed to fetch email pipeline status');
      throw error;
    }
  }

  async getRecords(params: EmailPipelineRecordsQuery): Promise<EmailPipelineRecordsResponse> {
    try {
      const query = buildQueryString({
        status: params.status,
        store: params.store,
        subject_contains: params.subject_contains,
        from_contains: params.from_contains,
        to_contains: params.to_contains,
        date_from: params.date_from,
        date_to: params.date_to,
        page: params.page && params.page > 0 ? params.page : undefined,
        page_size: params.page_size && params.page_size > 0 ? params.page_size : undefined,
      });
      const url = query ? `${API_URLS.EMAIL_PIPELINE_RECORDS}?${query}` : API_URLS.EMAIL_PIPELINE_RECORDS;
      const response = await apiGet(url);
      return response as EmailPipelineRecordsResponse;
    } catch (error) {
      console.error('Failed to fetch email pipeline records:', error);
      ErrorHandler.handleApiError(error, 'Failed to fetch email pipeline records');
      throw error;
    }
  }

  async getRecordDetails(messageId: string): Promise<EmailPipelineRecordDetailsResponse> {
    try {
      const response = await apiGet(API_URLS.EMAIL_PIPELINE_RECORD_DETAILS(messageId));
      return response as EmailPipelineRecordDetailsResponse;
    } catch (error) {
      console.error('Failed to fetch email pipeline record details:', error);
      ErrorHandler.handleApiError(error, 'Failed to fetch email pipeline record details');
      throw error;
    }
  }

  async getAttachmentBlob(path: string): Promise<Blob> {
    try {
      const response = await api.get(API_URLS.COMPLETE_STATIC_FILE_URL(path), { responseType: 'blob' });
      return response.data as Blob;
    } catch (error) {
      console.error('Failed to download email pipeline attachment:', error);
      ErrorHandler.handleApiError(error, 'Failed to download email pipeline attachment');
      throw error;
    }
  }

  async processMessage(messageId: string, force: boolean): Promise<EmailPipelineProcessResponse> {
    try {
      const response = await apiPost(API_URLS.EMAIL_PIPELINE_PROCESS_MESSAGE(messageId, force), {});
      const typedResponse = response as EmailPipelineProcessResponse;
      ensureProcessMessageSucceeded(typedResponse);
      return typedResponse;
    } catch (error) {
      console.error('Failed to process email pipeline message:', error);
      ErrorHandler.handleApiError(error, 'Failed to process email pipeline message');
      throw error;
    }
  }
}

const emailPipelineService = new EmailPipelineService();
export default emailPipelineService;
